import { useState, useEffect } from 'react';
import cadenceGqlCall from '../utils/cadence-gql-call';
import { ORG_DOMAIN_STATUS_QUERY } from '../utils/cadence-queries';

/**
 * Read-only hook for checking the org's sending domain status.
 * Returns 'not_configured' | 'pending' | 'configured'.
 *
 * Fetches via the cadences GraphQL API (server-to-server to Outreach) rather
 * than calling the Outreach REST endpoint directly. The direct endpoint is
 * admin-gated; non-owner users receive a 403, which previously caused the
 * domain verification warning banner to be suppressed for all non-admins.
 */
const useDomainStatus = () => {
  const [status, setStatus] = useState('configured'); // optimistic default — avoids false warnings on load
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState(null);

  useEffect(() => {
    let cancelled = false;
    cadenceGqlCall(ORG_DOMAIN_STATUS_QUERY)
      .then((data) => {
        if (cancelled) return;
        const result = data?.orgDomainStatus;
        // Normalize to lowercase — the backend returns a Strawberry enum whose GraphQL
        // representation is the Python name (e.g. NOT_CONFIGURED), not the value.
        const raw = result?.status?.toLowerCase();
        // 'error' means the cadences service couldn't reach Outreach — fail open
        // (same behaviour as the old REST error handler) to avoid blocking users
        // when the Outreach service is temporarily unavailable.
        const resolved = raw === 'error' || !raw ? 'configured' : raw;
        setStatus(resolved);
        // Reconstruct a domain-like object so consumers that use domain.name or
        // domain.default_sending_email continue to work.
        // Note: dkim_verified_at is not exposed by the cadences GQL endpoint — the
        // auto-resume banner that reads it will omit the date but won't crash.
        if (result?.domainName) {
          setDomain({
            name: result.domainName,
            default_sending_email: result.defaultSendingEmail || null,
            dkim_verified_at: null,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // Network/gateway error — fail open so a transient outage never blocks sends.
        console.warn('[useDomainStatus] Domain status check failed — defaulting to configured');
        setStatus('configured');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { status, loading, domain };
};

export default useDomainStatus;
